import { useMemo, useState, type KeyboardEvent, type Key } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, FileText, User } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";
import { formatDateBR, formatDateTimeBR } from "@/lib/date-utils";
import { cn } from "@/lib/utils";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";

type MedicalRecordRow = Database["public"]["Tables"]["medical_records"]["Row"];

type MedicalRecordWithOptionalJoins = MedicalRecordRow & {
  profiles?: { full_name?: string | null } | null;
  clinics?: { name?: string | null } | null;
};

interface RecentConsultationsCardProps {
  records?: MedicalRecordWithOptionalJoins[];
  isLoading?: boolean;
  maxItems?: number;
  title?: string;
  hideList?: boolean;
  dialogOpen?: boolean;
  onDialogOpenChange?: (open: boolean) => void;
  selectedRecordId?: string | null;
  onSelectedRecordIdChange?: (id: string | null) => void;
}

const normalizeText = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const formatDateTimeWithAs = (value?: string | null) => {
  const formatted = formatDateTimeBR(value);
  return formatted === "-" ? "-" : formatted.replace(" ", " às ");
};

const formatLabelValue = (label: string, value?: string | null, key?: Key) => (
  <div key={key} className="space-y-1">
    <p className="text-xs font-semibold text-muted-foreground">{label}</p>
    <p className="whitespace-pre-wrap break-words text-sm text-foreground">
      {value}
    </p>
  </div>
);

export function RecentConsultationsCard({
  records,
  isLoading,
  maxItems = 5,
  title = "Histórico de consultas",
  hideList = false,
  dialogOpen,
  onDialogOpenChange,
  selectedRecordId,
  onSelectedRecordIdChange,
}: RecentConsultationsCardProps) {
  const { t } = useTranslation("medicalRecords");
  const resolvedTitle = title === "Histórico de consultas" ? t("recentCard.defaultTitle") : title;
  const [selectedRecordState, setSelectedRecordState] = useState<MedicalRecordWithOptionalJoins | null>(null);
  const [isDialogOpenState, setIsDialogOpenState] = useState(false);
  const [isFullHistoryOpen, setIsFullHistoryOpen] = useState(false);

  const sorted = useMemo(() => {
    if (!records) return [];
    return [...records].sort((a, b) => {
      const dateA = Date.parse(a.consultation_date ?? "");
      const dateB = Date.parse(b.consultation_date ?? "");
      return (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA);
    });
  }, [records]);

  const limited = useMemo(() => sorted.slice(0, Math.max(0, maxItems)), [sorted, maxItems]);

  const resolvedSelectedRecord = useMemo(() => {
    if (selectedRecordId) {
      return (records ?? []).find((record) => record.id === selectedRecordId) ?? null;
    }
    return selectedRecordState;
  }, [records, selectedRecordId, selectedRecordState]);

  const resolvedDialogOpen = dialogOpen ?? isDialogOpenState;

  const setDialogOpen = (open: boolean) => {
    onDialogOpenChange?.(open);
    if (dialogOpen === undefined) {
      setIsDialogOpenState(open);
    }
    if (!open && selectedRecordId !== undefined) {
      onSelectedRecordIdChange?.(null);
    }
  };

  const setSelectedRecord = (record: MedicalRecordWithOptionalJoins | null) => {
    if (selectedRecordId !== undefined) {
      onSelectedRecordIdChange?.(record?.id ?? null);
      return;
    }
    setSelectedRecordState(record);
  };

  const handleOpen = (record: MedicalRecordWithOptionalJoins) => {
    setSelectedRecord(record);
    setDialogOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, record: MedicalRecordWithOptionalJoins) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpen(record);
    }
  };

  const recordTitle =
    normalizeText(resolvedSelectedRecord?.chief_complaint) ?? t("recentCard.defaultRecordTitle");

  const detailFields = useMemo(() => {
    if (!resolvedSelectedRecord) return [];
    const fields: Array<{ label: string; value: string }> = [];

    const add = (label: string, value?: string | null) => {
      const normalized = normalizeText(value);
      if (normalized) fields.push({ label, value: normalized });
    };

    add("Queixa principal", resolvedSelectedRecord.chief_complaint);
    add("História", resolvedSelectedRecord.history);
    add("Diagnóstico diferencial", resolvedSelectedRecord.presumptive_diagnosis);
    add("Diagnóstico definitivo", resolvedSelectedRecord.definitive_diagnosis);
    add("Tratamento", resolvedSelectedRecord.treatment);
    add("Recomendações", resolvedSelectedRecord.recommendations);
    add("Procedimentos", resolvedSelectedRecord.procedures);
    add("Medicações", resolvedSelectedRecord.medications);
    add("Dieta", resolvedSelectedRecord.diet);
    add("Ambiente", resolvedSelectedRecord.environment);

    return fields;
  }, [resolvedSelectedRecord]);

  const physicalExamFields = useMemo(() => {
    if (!resolvedSelectedRecord) return [];
    const fields: Array<{ label: string; value: string }> = [];

    const add = (label: string, value?: string | null) => {
      const normalized = normalizeText(value);
      if (normalized) fields.push({ label, value: normalized });
    };

    add("Comportamento", resolvedSelectedRecord.behavior);
    add("Mucosas", resolvedSelectedRecord.mucous_membranes);
    add("Hidratação", resolvedSelectedRecord.hydration_status);
    add("TRC", resolvedSelectedRecord.capillary_refill_time);
    add("Linfonodos", resolvedSelectedRecord.lymph_nodes);
    add("Ausculta respiratória", resolvedSelectedRecord.respiratory_auscultation);
    add("Ausculta cardíaca", resolvedSelectedRecord.cardiac_auscultation);
    add("Pele e pelagem", resolvedSelectedRecord.skin_coat);
    add("Sistema locomotor", resolvedSelectedRecord.locomotor_system);
    add("Palpação abdominal", resolvedSelectedRecord.abdominal_palpation);
    add("Outros sistemas", resolvedSelectedRecord.other_systems);

    return fields;
  }, [resolvedSelectedRecord]);

  const vitals = useMemo(() => {
    if (!resolvedSelectedRecord) return null;

    const hasAny =
      resolvedSelectedRecord.weight !== null ||
      resolvedSelectedRecord.temperature !== null ||
      resolvedSelectedRecord.heart_rate !== null ||
      resolvedSelectedRecord.respiratory_rate !== null;

    if (!hasAny) return null;

    return {
      weight: resolvedSelectedRecord.weight,
      temperature: resolvedSelectedRecord.temperature,
      heartRate: resolvedSelectedRecord.heart_rate,
      respiratoryRate: resolvedSelectedRecord.respiratory_rate,
    };
  }, [resolvedSelectedRecord]);

  const followUp = useMemo(() => {
    if (!resolvedSelectedRecord) return null;

    const date = normalizeText(resolvedSelectedRecord.follow_up_date);
    const notes = normalizeText(resolvedSelectedRecord.follow_up_notes);

    if (!date && !notes) return null;
    return { date, notes };
  }, [resolvedSelectedRecord]);

  const listContent = (
    <CardContent className="space-y-3 text-sm">
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : limited.length > 0 ? (
        <div className="divide-y rounded-md border">
          {limited.map((record) => {
            return (
              <div
                key={record.id}
                role="button"
                tabIndex={0}
                className={cn(
                  "bg-background px-3 py-3 transition-colors",
                  "first:rounded-t-md last:rounded-b-md hover:bg-secondary/50",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
                )}
                onClick={() => handleOpen(record)}
                onKeyDown={(event) => handleKeyDown(event, record)}
                aria-label={t("recentCard.openRecord", { date: formatDateTimeBR(record.consultation_date) })}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="min-w-0 flex-1 space-y-1 overflow-hidden">
                    <p className="text-xs font-semibold text-primary">
                      {formatDateTimeWithAs(record.consultation_date)}
                    </p>
                    <p className="block max-w-full truncate text-sm font-semibold text-foreground">
                      {normalizeText(record.chief_complaint) ?? t("recentCard.defaultRecordTitle")}
                    </p>
                    {normalizeText(record.definitive_diagnosis) ? (
                      <p className="line-clamp-1 text-xs text-muted-foreground">
                        {record.definitive_diagnosis}
                      </p>
                    ) : null}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted-foreground">
          {t("recentCard.empty")}
        </p>
      )}
      {!isLoading && sorted.length > limited.length ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setIsFullHistoryOpen(true)}
        >
          {t("recentCard.viewFullHistory")}
        </Button>
      ) : null}
    </CardContent>
  );

  return (
    <>
      {!hideList ? (
        <>
          <Card className="hidden lg:block">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-4 w-4" />
                {resolvedTitle}
              </CardTitle>
            </CardHeader>
            {listContent}
          </Card>

          <Collapsible className="lg:hidden !mt-0">
            <Card>
              <CardHeader className="p-4 lg:p-3 lg:pb-0">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="group flex w-full items-center justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                  >
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-4 w-4" />
                      {resolvedTitle}
                    </CardTitle>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                  </button>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                {listContent}
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </>
      ) : null}

      <Dialog open={isFullHistoryOpen} onOpenChange={setIsFullHistoryOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{resolvedTitle}</DialogTitle>
          </DialogHeader>
          <ScrollArea type="auto" className="h-[65vh] pr-3">
            <div className="divide-y rounded-md border">
              {sorted.map((record, index) => (
                <button
                  key={`${record.id}-${index}`}
                  type="button"
                  className="flex w-full min-w-0 items-center gap-3 bg-background px-3 py-3 text-left transition-colors first:rounded-t-md last:rounded-b-md hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                  onClick={() => {
                    setIsFullHistoryOpen(false);
                    handleOpen(record);
                  }}
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-xs font-semibold text-primary">
                      {formatDateTimeWithAs(record.consultation_date)}
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {normalizeText(record.chief_complaint) ?? t("recentCard.defaultRecordTitle")}
                    </p>
                    {normalizeText(record.definitive_diagnosis) ? (
                      <p className="line-clamp-1 text-xs text-muted-foreground">
                        {record.definitive_diagnosis}
                      </p>
                    ) : null}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog open={resolvedDialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-muted-foreground">
                {formatDateTimeWithAs(resolvedSelectedRecord?.consultation_date)}
              </span>
              <span className="text-lg font-semibold text-foreground">{recordTitle}</span>
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(85vh-160px)] px-6 pb-6">
            {resolvedSelectedRecord ? (
              <div className="space-y-6">
                <div className="grid gap-3 sm:grid-cols-2">
                  {resolvedSelectedRecord.profiles?.full_name ? (
                    <div className="flex items-start gap-2 rounded-md border bg-muted/20 p-3">
                      <User className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground">Veterinário</p>
                        <p className="truncate text-sm text-foreground">{resolvedSelectedRecord.profiles.full_name}</p>
                      </div>
                    </div>
                  ) : null}

                  {resolvedSelectedRecord.clinics?.name ? (
                    <div className="rounded-md border bg-muted/20 p-3">
                      <p className="text-xs font-semibold text-muted-foreground">Clínica</p>
                      <p className="truncate text-sm text-foreground">{resolvedSelectedRecord.clinics.name}</p>
                    </div>
                  ) : null}
                </div>

                {detailFields.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-foreground">Resumo</p>
                    </div>
                    <Separator />
                    <div className="grid gap-4">
                      {detailFields.map((field) => formatLabelValue(field.label, field.value, field.label))}
                    </div>
                  </div>
                ) : null}

                {physicalExamFields.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-foreground">Exame físico</p>
                    <Separator />
                    <div className="grid gap-4 sm:grid-cols-2">
                      {physicalExamFields.map((field) => (
                        <div key={field.label} className="space-y-1">
                          <p className="text-xs font-semibold text-muted-foreground">{field.label}</p>
                          <p className="whitespace-pre-wrap break-words text-sm text-foreground">
                            {field.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {vitals ? (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-foreground">Sinais vitais</p>
                    <Separator />
                    <div className="grid gap-3 sm:grid-cols-2">
                      {vitals.weight !== null ? (
                        <div className="rounded-md border bg-muted/20 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">Peso</p>
                          <p className="text-sm text-foreground">{vitals.weight} kg</p>
                        </div>
                      ) : null}
                      {vitals.temperature !== null ? (
                        <div className="rounded-md border bg-muted/20 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">Temperatura</p>
                          <p className="text-sm text-foreground">{vitals.temperature} °C</p>
                        </div>
                      ) : null}
                      {vitals.heartRate !== null ? (
                        <div className="rounded-md border bg-muted/20 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">Frequência cardíaca</p>
                          <p className="text-sm text-foreground">{vitals.heartRate} bpm</p>
                        </div>
                      ) : null}
                      {vitals.respiratoryRate !== null ? (
                        <div className="rounded-md border bg-muted/20 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">Frequência respiratória</p>
                          <p className="text-sm text-foreground">{vitals.respiratoryRate} irpm</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {followUp ? (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-foreground">Retorno</p>
                    <Separator />
                    <div className="space-y-3">
                      {followUp.date ? (
                        <div className="rounded-md border bg-muted/20 p-3">
                          <p className="text-xs font-semibold text-muted-foreground">Data</p>
                          <p className="text-sm text-foreground">{formatDateBR(followUp.date)}</p>
                        </div>
                      ) : null}
                      {followUp.notes ? formatLabelValue("Observações", followUp.notes) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </ScrollArea>

          <DialogFooter className="px-6 pb-6">
            <div className="flex w-full gap-3 justify-end">
              <div className="flex ">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
